// GC-MS mockup data for the gas analysis platform
// Ported from police-toxgas, stripped of runtime state and dataLoader dependency
// D1 Cotton 실측 데이터 통합: TIC 2410 points + 16 MS scan blocks (Step 1)

import type { SampleSource } from "@/stores/useAnalysisStore";
import type {
  FeatureData,
  MassSpectrumData,
  MSData,
  SimilarCase,
  TICData,
} from "@/types/spectrum";
import { getTICDataById, getMassSpectrumByRT } from "./realData/sampleRegistry";
import { getNearestScan } from "./realData/parseD1Cotton";

// D1 Cotton feature characteristics (12 features)
const featureCharacteristics = {
  F1: {
    name: "Gas Background (CO, N2, O2, CO2)",
    rtRange: [1.0, 1.4],
    rtPeak: 1.222,
    mzRange: [28, 50],
    basePeak: 44,
    intensityRange: [80, 100],
    peakHeight: 350,
    peakWidth: 0.06,
  },
  F2: {
    name: "Benzene",
    rtRange: [1.9, 2.3],
    rtPeak: 2.101,
    mzRange: [70, 85],
    basePeak: 78,
    intensityRange: [60, 95],
    peakHeight: 280,
    peakWidth: 0.08,
  },
  F3: {
    name: "Benzene Fragment",
    rtRange: [2.1, 2.4],
    rtPeak: 2.231,
    mzRange: [70, 85],
    basePeak: 77,
    intensityRange: [40, 70],
    peakHeight: 180,
    peakWidth: 0.06,
  },
  F4: {
    name: "Alkylbenzenes (C7-C8)",
    rtRange: [2.7, 3.0],
    rtPeak: 2.866,
    mzRange: [85, 100],
    basePeak: 91,
    intensityRange: [70, 100],
    peakHeight: 320,
    peakWidth: 0.1,
  },
  F5: {
    name: "n-Alkanes (C9-C10)",
    rtRange: [2.9, 3.3],
    rtPeak: 3.104,
    mzRange: [40, 90],
    basePeak: 85,
    intensityRange: [50, 85],
    peakHeight: 220,
    peakWidth: 0.12,
  },
  F6: {
    name: "n-Alkane/C9 Aromatic Mix",
    rtRange: [3.5, 3.8],
    rtPeak: 3.626,
    mzRange: [40, 100],
    basePeak: 85,
    intensityRange: [45, 80],
    peakHeight: 200,
    peakWidth: 0.14,
  },
  F7: {
    name: "C9 Aromatic Cluster (Ethylbenzene/Xylene)",
    rtRange: [3.6, 3.8],
    rtPeak: 3.697,
    mzRange: [90, 110],
    basePeak: 106,
    intensityRange: [55, 90],
    peakHeight: 250,
    peakWidth: 0.1,
  },
  F8: {
    name: "n-Alkane/C9 Aromatic Mix (C10-C12)",
    rtRange: [3.8, 4.0],
    rtPeak: 3.899,
    mzRange: [40, 110],
    basePeak: 85,
    intensityRange: [50, 85],
    peakHeight: 210,
    peakWidth: 0.12,
  },
  F9: {
    name: "Cotton Cellulose Pyrolysis (Furan/Ketone)",
    rtRange: [3.9, 4.1],
    rtPeak: 4.0,
    mzRange: [50, 100],
    basePeak: 55,
    intensityRange: [40, 75],
    peakHeight: 180,
    peakWidth: 0.08,
  },
  F10: {
    name: "Naphthenes (n-Alkylcyclohexanes)",
    rtRange: [4.1, 4.3],
    rtPeak: 4.172,
    mzRange: [50, 90],
    basePeak: 83,
    intensityRange: [45, 80],
    peakHeight: 190,
    peakWidth: 0.1,
  },
  F11: {
    name: "C9 Aromatic (TMB/Ethyltoluene)",
    rtRange: [4.3, 4.6],
    rtPeak: 4.421,
    mzRange: [100, 125],
    basePeak: 120,
    intensityRange: [50, 85],
    peakHeight: 200,
    peakWidth: 0.12,
  },
  F12: {
    name: "n-Alkane/C9 Aromatic Mix (C12+)",
    rtRange: [4.6, 4.8],
    rtPeak: 4.659,
    mzRange: [50, 125],
    basePeak: 105,
    intensityRange: [40, 75],
    peakHeight: 160,
    peakWidth: 0.14,
  },
};

// D1 Cotton features sample data
export const featuresSample: FeatureData[] = [
  {
    feature_id: "F1",
    rt: 1.222,
    peak_no: 1,
    area: 4567.2,
    top_mz: 44,
    predicted_substance: "Gas Background (CO, N2, O2, CO2)",
    matching_score: 99.2,
    ai_note: "RT 1.222 min; m/z 30, 32, 44; 가스 백그라운드 - 정상 연소 생성물",
    suspicious: false,
    smiles: "O=C=O",
  },
  {
    feature_id: "F2",
    rt: 2.101,
    peak_no: 2,
    area: 2834.7,
    top_mz: 78,
    predicted_substance: "Benzene",
    matching_score: 96.8,
    ai_note: "RT 2.101 min; m/z 77, 78; 방향족 화합물 - 불완전연소 산물",
    suspicious: true,
    smiles: "c1ccccc1",
  },
  {
    feature_id: "F3",
    rt: 2.231,
    peak_no: 3,
    area: 1234.5,
    top_mz: 77,
    predicted_substance: "Benzene Fragment",
    matching_score: 89.4,
    ai_note: "RT 2.231 min; m/z 77; 벤젠 분해 잔여물",
    suspicious: false,
    smiles: "c1ccccc1",
  },
  {
    feature_id: "F4",
    rt: 2.866,
    peak_no: 4,
    area: 3456.8,
    top_mz: 91,
    predicted_substance: "Alkylbenzenes (C7-C8)",
    matching_score: 95.3,
    ai_note:
      "RT 2.866 min; m/z 91, 92; 알킬벤젠류 + 톨루엔 핵심 이온 - 경유 성분 의심",
    suspicious: true,
    smiles: "Cc1ccccc1",
  },
  {
    feature_id: "F5",
    rt: 3.104,
    peak_no: 5,
    area: 2156.3,
    top_mz: 85,
    predicted_substance: "n-Alkanes (C9-C10)",
    matching_score: 91.7,
    ai_note: "RT 3.104 min; m/z 43, 85; n-알케인군 (C9-C10) - 석유계 탄화수소",
    suspicious: false,
    smiles: "CCCCCCCCC",
  },
  {
    feature_id: "F6",
    rt: 3.626,
    peak_no: 6,
    area: 1987.4,
    top_mz: 85,
    predicted_substance: "n-Alkane/C9 Aromatic Mix",
    matching_score: 88.9,
    ai_note: "RT 3.626 min; m/z 43, 71, 85, 91; n-알케인/C9 방향족 혼합물",
    suspicious: false,
    smiles: "CCc1ccccc1",
  },
  {
    feature_id: "F7",
    rt: 3.697,
    peak_no: 7,
    area: 2678.1,
    top_mz: 106,
    predicted_substance: "C9 Aromatic Cluster (Ethylbenzene/Xylene)",
    matching_score: 94.1,
    ai_note:
      "RT 3.697 min; m/z 91, 105, 106; C9 방향족 클러스터 - 에틸벤젠/자일렌군",
    suspicious: true,
    smiles: "Cc1ccccc1C",
  },
  {
    feature_id: "F8",
    rt: 3.899,
    peak_no: 8,
    area: 2345.6,
    top_mz: 85,
    predicted_substance: "n-Alkane/C9 Aromatic Mix (C10-C12)",
    matching_score: 90.5,
    ai_note:
      "RT 3.899 min; m/z 41, 43, 57, 85, 91, 106; n-알케인(C10-C12)/C9 방향족 혼합",
    suspicious: false,
    smiles: "CCCCCCCCCC",
  },
  {
    feature_id: "F9",
    rt: 4.0,
    peak_no: 9,
    area: 1567.8,
    top_mz: 55,
    predicted_substance: "Cotton Cellulose Pyrolysis",
    matching_score: 87.2,
    ai_note: "RT 4.000 min; m/z 55, 97; 면 셀룰로오스 열분해 - 퓨란/케톤계",
    suspicious: false,
    smiles: "O1CCCC1",
  },
  {
    feature_id: "F10",
    rt: 4.172,
    peak_no: 10,
    area: 1876.9,
    top_mz: 83,
    predicted_substance: "Naphthenes (PAHs precursor)",
    matching_score: 92.8,
    ai_note:
      "RT 4.172 min; m/z 55, 57, 71, 83; 나프텐류 - 경유 불완전연소 시 풍부",
    suspicious: true,
    smiles: "C1CCCCC1",
  },
  {
    feature_id: "F11",
    rt: 4.421,
    peak_no: 11,
    area: 2234.7,
    top_mz: 120,
    predicted_substance: "C9 Aromatic (TMB/Ethyltoluene)",
    matching_score: 93.6,
    ai_note:
      "RT 4.421 min; m/z 105, 106, 120; C9 방향족 - TMB/에틸톨루엔, 경유 원성분",
    suspicious: true,
    smiles: "Cc1ccc(C)cc1C",
  },
  {
    feature_id: "F12",
    rt: 4.659,
    peak_no: 12,
    area: 1432.1,
    top_mz: 105,
    predicted_substance: "n-Alkane/C9 Aromatic Mix (C12+)",
    matching_score: 86.4,
    ai_note: "RT 4.659 min; m/z 57, 105, 120; n-알케인(C12+)/C9 방향족 혼합",
    suspicious: false,
    smiles: "CCCCCCCCCCCC",
  },
];

// ─────────────────────────────────────────────────────────────────
// iter2.2: Demo Scenario Mode 시나리오별 features set
// ─────────────────────────────────────────────────────────────────

/**
 * arson-strong 시나리오 — 강한 방화 의심 패턴 (BTEX + 알킬벤젠 다량 검출).
 * 시연 시 ?scenario=arson-strong 으로 노출, RoMSH 시뮬 prob 0.85.
 */
export const featuresSample_arsonStrong: FeatureData[] = [
  {
    feature_id: "F1",
    rt: 2.8,
    peak_no: 1,
    area: 18000,
    top_mz: 78,
    predicted_substance: "Benzene",
    matching_score: 96,
    ai_note: "BTEX — 강한 방향족 signal",
    suspicious: true,
    smiles: "c1ccccc1",
    jsd_distance: 0.08,
  },
  {
    feature_id: "F2",
    rt: 3.5,
    peak_no: 2,
    area: 22000,
    top_mz: 91,
    predicted_substance: "Toluene",
    matching_score: 94,
    ai_note: "BTEX — 강한 방향족 signal",
    suspicious: true,
    smiles: "Cc1ccccc1",
    jsd_distance: 0.05,
  },
  {
    feature_id: "F3",
    rt: 4.5,
    peak_no: 3,
    area: 19500,
    top_mz: 106,
    predicted_substance: "Ethylbenzene",
    matching_score: 92,
    ai_note: "BTEX — 강한 방향족 signal",
    suspicious: true,
    smiles: "CCc1ccccc1",
    jsd_distance: 0.07,
  },
  {
    feature_id: "F4",
    rt: 4.8,
    peak_no: 4,
    area: 16500,
    top_mz: 106,
    predicted_substance: "m-Xylene",
    matching_score: 89,
    ai_note: "BTEX — 강한 방향족 signal",
    suspicious: true,
    smiles: "Cc1ccccc1C",
    jsd_distance: 0.09,
  },
  {
    feature_id: "F5",
    rt: 5.2,
    peak_no: 5,
    area: 14000,
    top_mz: 106,
    predicted_substance: "p-Xylene",
    matching_score: 87,
    ai_note: "BTEX — 강한 방향족 signal",
    suspicious: true,
    smiles: "Cc1ccc(C)cc1",
    jsd_distance: 0.11,
  },
];

/**
 * natural-strong 시나리오 — 자연 발화 패턴 (n-알칸 + 시클로 only).
 * 시연 시 ?scenario=natural-strong 으로 노출, RoMSH 시뮬 prob 0.18.
 */
export const featuresSample_naturalStrong: FeatureData[] = [
  {
    feature_id: "F1",
    rt: 6.2,
    peak_no: 1,
    area: 25000,
    top_mz: 84,
    predicted_substance: "n-Decane",
    matching_score: 91,
    ai_note: "n-알칸 직쇄 (자연 발화 마커)",
    suspicious: false,
    smiles: "CCCCCCCCCC",
    jsd_distance: 0.12,
  },
  {
    feature_id: "F2",
    rt: 7.4,
    peak_no: 2,
    area: 28000,
    top_mz: 84,
    predicted_substance: "n-Undecane",
    matching_score: 89,
    ai_note: "n-알칸 직쇄 (자연 발화 마커)",
    suspicious: false,
    smiles: "CCCCCCCCCCC",
    jsd_distance: 0.14,
  },
  {
    feature_id: "F3",
    rt: 8.8,
    peak_no: 3,
    area: 24500,
    top_mz: 84,
    predicted_substance: "n-Dodecane",
    matching_score: 88,
    ai_note: "n-알칸 직쇄 (자연 발화 마커)",
    suspicious: false,
    smiles: "CCCCCCCCCCCC",
    jsd_distance: 0.13,
  },
  {
    feature_id: "F4",
    rt: 10.5,
    peak_no: 4,
    area: 19000,
    top_mz: 84,
    predicted_substance: "Naphthenes",
    matching_score: 82,
    ai_note: "포화 환 (자연 분해 산물)",
    suspicious: false,
    smiles: "C1CCCCC1",
    jsd_distance: 0.18,
  },
];

/**
 * iter2.2: 시나리오별 mocked RoMSH probability. 실제 RoMSH 모델 출력 override.
 */
export const SCENARIO_MOCKED_PROB: Record<string, number> = {
  "arson-strong": 0.85,
  "natural-strong": 0.18,
};

/**
 * iter2.2: 시나리오별 similar cases stub. arson-strong는 방화 사건,
 * natural-strong은 자연 발화/자연분해 사건.
 */
export const similarCasesSample_arsonStrong: SimilarCase[] = [
  {
    case_id: "Arson_BTEX_2024_087",
    similarity: 0.93,
    incident_type: "Gasoline arson",
    dominant_compounds: "['Benzene', 'Toluene', 'Xylene']",
    date: "2024-09-08",
  },
  {
    case_id: "Arson_Toluene_2024_071",
    similarity: 0.89,
    incident_type: "Solvent arson",
    dominant_compounds: "['Toluene', 'Ethylbenzene']",
    date: "2024-07-19",
  },
  {
    case_id: "Arson_Mixed_2024_142",
    similarity: 0.82,
    incident_type: "Mixed accelerant",
    dominant_compounds: "['BTEX', 'C9 Aromatics']",
    date: "2024-11-03",
  },
];

export const similarCasesSample_naturalStrong: SimilarCase[] = [
  {
    case_id: "Natural_Decay_2024_054",
    similarity: 0.88,
    incident_type: "Natural decomposition",
    dominant_compounds: "['n-Alkanes', 'Cyclic']",
    date: "2024-06-15",
  },
  {
    case_id: "Spontaneous_2024_028",
    similarity: 0.81,
    incident_type: "Spontaneous combustion",
    dominant_compounds: "['n-Decane', 'n-Undecane']",
    date: "2024-04-22",
  },
];

// Similar cases sample data
export const similarCasesSample: SimilarCase[] = [
  {
    case_id: "Cotton_Fire_D1_001",
    similarity: 0.91,
    incident_type: "Textile Fire",
    dominant_compounds: "['Carbon Dioxide', 'Benzene', 'Toluene']",
    date: "2024-08-12",
  },
  {
    case_id: "Nylon_Fire_D6_002",
    similarity: 0.84,
    incident_type: "Synthetic Fire",
    dominant_compounds: "['Pentane derivatives', 'Aromatic compounds']",
    date: "2024-08-17",
  },
  {
    case_id: "Polyester_Fire_D11_003",
    similarity: 0.78,
    incident_type: "Synthetic Fire",
    dominant_compounds: "['Benzene', 'Toluene', 'Xylene']",
    date: "2024-08-19",
  },
  {
    case_id: "Linen_Fire_D16_004",
    similarity: 0.72,
    incident_type: "Natural Fiber Fire",
    dominant_compounds: "['Carbon Dioxide', 'Organic acids']",
    date: "2024-08-21",
  },
];

// Fragment peaks for each feature (based on D1 Cotton experiment MS data)
export function getFragmentPeaks(
  featureId: string,
): Array<{ mz: number; relativeIntensity: number }> {
  switch (featureId) {
    case "F1": // Gas Background
      return [
        { mz: 30, relativeIntensity: 0.7 },
        { mz: 32, relativeIntensity: 0.8 },
        { mz: 28, relativeIntensity: 0.5 },
      ];
    case "F2": // Benzene
      return [
        { mz: 77, relativeIntensity: 0.9 },
        { mz: 51, relativeIntensity: 0.4 },
        { mz: 50, relativeIntensity: 0.3 },
      ];
    case "F3": // Benzene Fragment
      return [
        { mz: 51, relativeIntensity: 0.5 },
        { mz: 50, relativeIntensity: 0.3 },
      ];
    case "F4": // Alkylbenzenes (C7-C8)
      return [
        { mz: 92, relativeIntensity: 0.6 },
        { mz: 65, relativeIntensity: 0.4 },
        { mz: 39, relativeIntensity: 0.3 },
      ];
    case "F5": // n-Alkanes (C9-C10)
      return [
        { mz: 43, relativeIntensity: 0.8 },
        { mz: 57, relativeIntensity: 0.6 },
        { mz: 71, relativeIntensity: 0.5 },
      ];
    case "F6": // n-Alkane/C9 Aromatic Mix
      return [
        { mz: 43, relativeIntensity: 0.7 },
        { mz: 71, relativeIntensity: 0.6 },
        { mz: 91, relativeIntensity: 0.4 },
      ];
    case "F7": // C9 Aromatic Cluster
      return [
        { mz: 91, relativeIntensity: 0.8 },
        { mz: 105, relativeIntensity: 0.7 },
        { mz: 77, relativeIntensity: 0.5 },
      ];
    case "F8": // n-Alkane/C9 Aromatic Mix (C10-C12)
      return [
        { mz: 41, relativeIntensity: 0.6 },
        { mz: 43, relativeIntensity: 0.7 },
        { mz: 57, relativeIntensity: 0.8 },
        { mz: 91, relativeIntensity: 0.5 },
        { mz: 106, relativeIntensity: 0.4 },
      ];
    case "F9": // Cotton Cellulose Pyrolysis
      return [
        { mz: 97, relativeIntensity: 0.6 },
        { mz: 39, relativeIntensity: 0.4 },
        { mz: 29, relativeIntensity: 0.3 },
      ];
    case "F10": // Naphthenes
      return [
        { mz: 55, relativeIntensity: 0.8 },
        { mz: 57, relativeIntensity: 0.7 },
        { mz: 71, relativeIntensity: 0.6 },
      ];
    case "F11": // C9 Aromatic (TMB/Ethyltoluene)
      return [
        { mz: 105, relativeIntensity: 0.8 },
        { mz: 106, relativeIntensity: 0.6 },
        { mz: 91, relativeIntensity: 0.5 },
      ];
    case "F12": // n-Alkane/C9 Aromatic Mix (C12+)
      return [
        { mz: 57, relativeIntensity: 0.7 },
        { mz: 120, relativeIntensity: 0.5 },
        { mz: 91, relativeIntensity: 0.4 },
      ];
    default:
      return [];
  }
}

// Generate fallback TIC data based on 12-feature D1 Cotton pattern
export function generateFallbackTICData(): TICData[] {
  const ticData: TICData[] = [];
  const totalPoints = 1000;
  const rtStep = 0.01;

  for (let i = 0; i < totalPoints; i++) {
    const rt = i * rtStep;
    let intensity = 0;

    // Generate peaks based on 12 feature characteristics (D1 Cotton data pattern)
    Object.values(featureCharacteristics).forEach((feature) => {
      const gaussianPeak =
        feature.peakHeight *
        Math.exp(-(((rt - feature.rtPeak) / feature.peakWidth) ** 2));
      intensity += gaussianPeak;
    });

    // Baseline (minimum observed in real data)
    intensity += 15;

    // Minor peaks observed in real D1 Cotton data
    // Early gas background
    if (rt >= 0.8 && rt <= 1.0) {
      intensity += 25 * Math.exp(-(((rt - 0.9) / 0.05) ** 2));
    }

    // Mid-range minor compounds
    if (rt >= 3.25 && rt <= 3.45) {
      intensity += 20 * Math.exp(-(((rt - 3.35) / 0.06) ** 2));
    }

    // High molecular weight compounds region
    if (rt >= 4.8 && rt <= 5.2) {
      intensity += 30 * Math.exp(-(((rt - 5.0) / 0.1) ** 2));
    }

    ticData.push({
      rt: parseFloat(rt.toFixed(2)),
      intensity: Math.max(0, Math.round(intensity)),
    });
  }

  return ticData;
}

// Generate fallback MS data for all 12 features
export function generateMockupMSData(): MSData[] {
  const data: MSData[] = [];

  Object.entries(featureCharacteristics).forEach(
    ([featureId, characteristics]) => {
      const { rtPeak, basePeak, intensityRange, rtRange } = characteristics;

      // Base peak data point (peak center)
      data.push({
        featureId: featureId,
        mz: basePeak,
        intensity: intensityRange[1],
        rt: rtPeak,
      });

      // Data points around the peak (within RT range)
      const numPeakPoints = 8;
      const rtStart = rtRange[0];
      const rtEnd = rtRange[1];
      const rtStep = (rtEnd - rtStart) / (numPeakPoints - 1);

      for (let i = 0; i < numPeakPoints; i++) {
        const currentRT = rtStart + rtStep * i;

        // Intensity decreases with distance from peak (Gaussian)
        const distanceFromPeak = Math.abs(currentRT - rtPeak);
        const maxDistance = Math.max(rtPeak - rtStart, rtEnd - rtPeak);
        const intensityFactor = Math.exp(
          -((distanceFromPeak / maxDistance) ** 2),
        );

        // Points around the base peak
        if (currentRT !== rtPeak) {
          data.push({
            featureId: featureId,
            mz: basePeak,
            intensity: Math.round(intensityRange[1] * intensityFactor * 0.8),
            rt: parseFloat(currentRT.toFixed(3)),
          });
        }

        // Fragment peaks distributed across RT range
        const fragmentPeaks = getFragmentPeaks(featureId);
        fragmentPeaks.forEach(({ mz, relativeIntensity }) => {
          data.push({
            featureId: featureId,
            mz: mz,
            intensity: Math.round(
              intensityRange[1] * relativeIntensity * intensityFactor * 0.6,
            ),
            rt: parseFloat(currentRT.toFixed(3)),
          });
        });
      }
    },
  );

  return data;
}

// Generate mass spectrum data for a specific feature
// 실측 데이터(MS1.csv nearest scan) 우선; featuresSample의 RT로 nearest scan 검색
export function generateMassSpectrumData(
  featureId: string,
): MassSpectrumData[] {
  const feature = featuresSample.find((f) => f.feature_id === featureId);
  if (feature) {
    const scan = getNearestScan(feature.rt);
    if (scan && scan.data.length > 0) {
      // m/z 순 정렬하여 반환
      return [...scan.data].sort((a, b) => a.mz - b.mz);
    }
  }
  // featuresSample에 없는 ID — 합성 fallback (하위 호환)
  const characteristics =
    featureCharacteristics[featureId as keyof typeof featureCharacteristics];
  if (!characteristics) return [];
  const { basePeak, intensityRange } = characteristics;
  const spectrumData: MassSpectrumData[] = [];
  spectrumData.push({ mz: basePeak, intensity: intensityRange[1] * 100 });
  const fragmentPeaks = getFragmentPeaks(featureId);
  for (const { mz, relativeIntensity } of fragmentPeaks) {
    spectrumData.push({
      mz,
      intensity: Math.round(intensityRange[1] * relativeIntensity * 100),
    });
  }
  return spectrumData.sort((a, b) => a.mz - b.mz);
}

// Generate mass spectrum for an arbitrary RT (nearest scan)
// 해석 (a): TIC 임의 RT 클릭 → 실측 nearest scan 반환
export function generateMassSpectrumByRT(rt: number): MassSpectrumData[] {
  const scan = getNearestScan(rt);
  if (scan && scan.data.length > 0) {
    return [...scan.data].sort((a, b) => a.mz - b.mz);
  }
  return [];
}

// ─── Source-aware helpers (Step 3: 20 samples — sampleRegistry 위임) ──────────

/**
 * sampleSource에 따라 TIC 데이터를 반환한다.
 * 모든 20 samples 지원 — sampleRegistry.getTICDataById 위임
 */
export function getTICDataBySource(source: SampleSource): TICData[] {
  return getTICDataById(source);
}

/**
 * sampleSource에 따라 nearest MS scan을 반환한다.
 * D19 (MS 결측) 또는 스캔 없음 시 빈 배열 반환.
 * 모든 20 samples 지원 — sampleRegistry.getMassSpectrumByRT 위임
 */
export function getMassSpectrumByRTBySource(
  rt: number,
  source: SampleSource,
): MassSpectrumData[] {
  return getMassSpectrumByRT(source, rt);
}

// Pre-generated static data
export const mockupMSData: MSData[] = generateMockupMSData();

// D1 Cotton 실측 TIC (2410 points, RT 0.095~14.393 min)
// 합성 Gaussian 대체. 레지스트리로 위임.
export const mockupTICData: TICData[] = getTICDataById("cotton-d1");

// 실측 MS scan blocks 재출내보내기 (하위 호환 접근용)
export {
  d1CottonMSScans,
  getNearestScan,
} from "./realData/parseD1Cotton";
// nylon D6 re-export
export {
  d6NylonTIC,
  getNearestNylonScan,
} from "./realData/parseD6Nylon";

// Data statistics
export const mockupDataStats = {
  totalPoints: mockupMSData.length,
  featureCounts: mockupMSData.reduce(
    (acc, point) => {
      acc[point.featureId] = (acc[point.featureId] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  ),
  mzRange: {
    min: Math.min(...mockupMSData.map((p) => p.mz)),
    max: Math.max(...mockupMSData.map((p) => p.mz)),
  },
  rtRange: {
    min: Math.min(...mockupMSData.map((p) => p.rt)),
    max: Math.max(...mockupMSData.map((p) => p.rt)),
  },
  intensityRange: {
    min: Math.min(...mockupMSData.map((p) => p.intensity)),
    max: Math.max(...mockupMSData.map((p) => p.intensity)),
  },
};

// Helper: filter data by feature ID
export function getDataByFeatureId(featureId: string): MSData[] {
  return mockupMSData.filter((point) => point.featureId === featureId);
}

// Helper: filter data by RT range
export function getDataByRTRange(minRT: number, maxRT: number): MSData[] {
  return mockupMSData.filter((point) => point.rt >= minRT && point.rt <= maxRT);
}

// Helper: filter data by m/z range
export function getDataByMZRange(minMZ: number, maxMZ: number): MSData[] {
  return mockupMSData.filter((point) => point.mz >= minMZ && point.mz <= maxMZ);
}
